import React, { Fragment } from 'react';
import { withFormik } from 'formik';
import { head, path, defaultTo, isEmpty } from 'ramda';
import Select from 'react-select';
import cn from 'classnames';

import styles from './styles.m.scss';
import image from '../../../../../../images/course-materials-visual.png';

import { toCurrency } from '../../../../../util/helpers';
import { apiUpdateRegistration } from '../../../../../api/registration';

import Footer from '../../../../../components/Accordion/Footer';
import Checkbox from '../../../../../components/Forms/Checkbox';
import Radio from '../../../../../components/Forms/Radio';
import FormGroup from '../../../../../components/Forms/FormGroup';
import { handleError, handleSuccess } from '../../../../../util/forms';
import { isNilOrEmpty } from '../../../../../util/redux';
import magic from '../../../../../util/magic';

const CourseMaterials = ({
  registration,
  pathway,
  errors,
  touched,
  values,
  handleBlur,
  handleChange,
  handleSubmit,
  isSubmitting,
  isValid,
  status,
  setFieldValue,
  setStep,
  step,
}) => {
  const isFFCPathway = pathway.key == magic.ffcPathway;
  const isSelfStudyPathway = pathway.key == magic.selfStudyPathway;

  const materialOptions = defaultTo([], path(['courseMaterials'], pathway));
  const events = defaultTo([], path(['events'], pathway));
  const webinarOptions = events.filter((event) => event.type == magic.eventWebinar);
  const onSiteOptions = events.filter((event) => event.type == magic.eventOnsite);

  return (
    <form>
      <h2 className={styles.stepHeader}>
        {isFFCPathway
          ? 'Please select your preferred training dates below'
          : 'Please order the materials from the options below'}
      </h2>
      <FormGroup label={isFFCPathway ? 'Choose either on-site or webinar training' : 'Educational Requirements'}>
        {isSelfStudyPathway && (
          <p className="dark-blue-heading font-weight-bold">Please select ONE study material below.</p>
        )}
        {errors.options && (
          <span className="invalid-feedback">
            <strong>{errors.options}</strong>
          </span>
        )}
        {materialOptions &&
          materialOptions.map((materialOption) => {
            return (
              <Radio
                key={materialOption.id}
                name={`options.${materialOption.id}`}
                value={materialOption.id}
                checked={head(values.options)}
                label={`${materialOption.name} (${toCurrency(materialOption.price)})`}
                onChange={() => setFieldValue('options', [materialOption.id])}
              />
            );
          })}
        {onSiteOptions.length > 0 && isFFCPathway && (
          <Fragment>
            <label className="mt-3">On Site Review Course - Includes 2 days of review and exam</label>
            <Select
              simpleValue
              onChange={(value) => setFieldValue('onSiteId', value)}
              options={onSiteOptions.map((onSite) => ({ value: onSite.id, label: onSite.name }))}
              value={values.onSiteId}
            />
          </Fragment>
        )}

        {webinarOptions.length > 0 && isFFCPathway && (
          <Fragment>
            <label className="mt-3">Web Based Review Course</label>
            <Select
              simpleValue
              onChange={(value) => setFieldValue('webinarId', value)}
              options={webinarOptions.map((webinar) => ({ value: webinar.id, label: webinar.name }))}
              value={values.webinarId}
            />
          </Fragment>
        )}

        {isFFCPathway && (
          <Fragment>
            <label className="mt-3">
              <p>I understand and agree that The FFC®️ certification requires a
              financial knowledge co-requisite and that I must provide evidence of
              the successful completion of an approved co-requisite before
              receiving my FFC®️ certification and must be completed and provided
              within the maximum timeline of 24 months coinciding with the maximum
              amount of time to complete FFC®️ training.</p>

            <p>Currently approved programs include: </p>
            <ul>
              <li>AFCPE’s Money Management Essentials course (MME) (Our recommendation for minimum, entry-level)</li>
              <li>AFC®️ (Our usual recommendation)</li>
              <li>CFP®️</li>
              <li>ChFC®️</li>
              <li>CFA®️</li>
              <li>CPA®️</li>
            </ul>

              I understand I have 6 months to complete ALL requirements of Module 1: Coaching Essentials. I will have
              the option to pay a $200 fee to extend my deadline by 3 months, but NO additional extensions will be
              granted to complete the Module 1: Coaching Essentials course.
            </label>
            <Checkbox
              name="extensionUnderstanding"
              label="I agree"
              checked={values.extensionUnderstanding}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <label className="mt-3">
              If I choose to pursue FFC Certification, I understand I have 2 years to complete ALL requirements of the
              program. I may register for each Module separately, but my deadline to complete the program corresponds
              with my entry into Module 1: Coaching Essentials. If additional time is needed to complete a module, I
              have the option to pay a $200 fee to extend the deadline for that particular module by 3 months.
              Extensions cannot be compounded at any point in the program.
            </label>
            <Checkbox
              name="lengthUnderstanding"
              label="I agree"
              checked={values.lengthUnderstanding}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <label className="mt-3">
              During core training, you will be interacting and practicing with your Instructor and colleagues so you
              MUST have access to an environment that is conducive to hearing and sharing and MUST have a microphone, or
              headphones with a built-in microphone, prepared in advance.
            </label>
            <Checkbox
              name="correctEnvironment"
              label="I agree"
              checked={values.correctEnvironment}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <label className="mt-3">
              During the practicum coaching sessions, you will need to identify a friend, colleague, family member or
              pro-bono client to work with. This person will be your practice client and MUST be willing to commit to
              FOUR total sessions with you.
            </label>
            <Checkbox
              name="practiceClient"
              label="I agree"
              checked={values.practiceClient}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Fragment>
        )}
      </FormGroup>
      <Footer step={step} setStep={setStep} isValid={isValid} isSubmitting={isSubmitting} handleSubmit={handleSubmit} />
    </form>
  );
};

export default withFormik({
  handleSubmit: (payload, formProps) => {
    const { props } = formProps;

    apiUpdateRegistration(props.registration.id, { stepName: props.name, ...payload })
      .then(({ data }) => props.updateRegistration({ ...data, courseMaterials: defaultTo([], data.courseMaterials) }))
      .then(() => handleSuccess(formProps))
      .then(() => props.setStep(props.step + 1))
      .catch((err) => handleError(formProps, err));
  },
  mapPropsToValues: ({ registration }) => {
    const events = defaultTo([], registration.events);
    const webinar = defaultTo(
      {},
      events.find((event) => event.type == magic.eventWebinar),
    );
    const onSite = defaultTo(
      {},
      events.find((event) => event.type == magic.eventOnsite),
    );

    return {
      options: defaultTo([], path(['courseMaterials'], registration)).map((option) => option.productId),
      webinarId: defaultTo('', webinar.productId),
      onSiteId: defaultTo('', onSite.productId),
      correctEnvironment: false,
      practiceClient: false,
    };
  },
  validate: (values, props) => {
    const {
      options,
      webinarId,
      onSiteId,
      extensionUnderstanding,
      lengthUnderstanding,
      correctEnvironment,
      practiceClient,
    } = values;
    const { pathway } = props;
    const errors = {};
    const isFFCPathway = pathway.key == magic.ffcPathway;

    if (isFFCPathway && isEmpty(options) && isNilOrEmpty(webinarId) && isNilOrEmpty(onSiteId)) {
      errors.options = 'You must choose a training date';
    }

    if (isFFCPathway && (!extensionUnderstanding || !lengthUnderstanding || !correctEnvironment || !practiceClient)) {
      errors.agree = 'You must agree to the terms';
    }

    return errors;
  },
  isInitialValid: ({ initialValues, validate }) => isEmpty(validate(initialValues)),
  validateOnChange: true,
})(CourseMaterials);
